<script>
import appConfig from "@/app.config";
import { mapActions, mapGetters, mapState } from "vuex";
import {
  required,
  email,
  numeric,
  minLength,
  maxLength,
  requiredIf,
} from "vuelidate/lib/validators";
import { FormWizard, TabContent, WizardButton } from "vue-form-wizard";
import isValidDomain from "is-valid-domain";
import Multiselect from "vue-multiselect";
import VueCountryCode from "vue-country-code";
import UserService from "../../../services/userService";
import "vue-form-wizard/dist/vue-form-wizard.min.css";

import { subdomainIssue, unavailableReasonFrom } from "@/utils/subdomainFeedback";
export default {
  page: {
    title: "Register",
    meta: [{ name: "description", content: appConfig.description }],
  },
  components: {
    FormWizard,
    TabContent,
    Multiselect,
    VueCountryCode,
    WizardButton
  },
  data() {
    return {
      typeOptions: ['Kiosk', 'Door'],
      userCountryCode: null,
      companyCountryCode: null,
            device: "",
      deviceOptions: [
        { id: "LT-Zentron8", name: "LT-Zentron8" },
        { id: "LD-AITemp", name: "LD-AITemp" },
        { id: "Zentron 15", name: "Zentron 15" },
      ],
      form: {
        typeValue: '',
        user: {
          first_name: "",
          last_name: "",
          phone: "",
          email: null,
        },
        entity: {
          name: "",
          subdomain: null,
          company_email: "",
          company_phone: "",
          package: "Standalone",
        },
      },
      submitted: false,
      regError: null,
      tryingToRegister: false,
      isRegisterError: false,
      registerSuccess: false,
      inputTyping: false,
      phoneInputTyping: false,
      subdomainInputTyping: false,
      servers: [],
      server: null,
      subdomain: "",
      phone_input: null,
      phone_input_company: null,
      subdomainValue: null,
      openKioskInfo: false,
      terms_and_conditions : false,
      subdomainInputValid : null,
      // CL-18308: WHY the subdomain is unavailable -- 'taken' or 'reserved'.
      subdomainUnavailableReason: null,
    };
  },
  validations: {
    form: {
      user: {
        first_name: {
          required,
          maxLength: maxLength(20),
        },
        last_name: {
          required,
          maxLength: maxLength(20),
        },
        email: {
          required: requiredIf(function() {
            const { phone_input } = this;
            return !phone_input;
          }),
          email,
        },
      },
      entity: {
        name: { required, maxLength: maxLength(50) },
        subdomain: { required },
        company_email: { email },
        package: { required },
      },
    },
    server: { required },
    subdomainValue: { 
      required,
      minLength: minLength(3),
      maxLength: maxLength(63),
      containsSpace: function(value) {
        return /[^a-zA-Z0-9-]/.test(value);
      },
      validCharacters: function(value) {
        return/^[a-z0-9-]+$/.test(value);
      },
      containsHyphenAtStart: function(value) {
        return value.startsWith('-') || value.endsWith('-');
      },
      containsDoubleHyphen: function(value) {
        return value.includes('--');
      },
     },
    phone_input: {
      required: requiredIf(function() {
        const {
          form: {
            user: { email },
          },
        } = this;
        return !email;
      }),
      numeric,
    },
    phone_input_company: { numeric },
  },
  watch: {
    "form.entity.package"(kioskPackage) {
      this.saveKioskPackage(kioskPackage);
    },
    "form.user.password"(pass) {
      this.saveUserPassword(pass);
    },
    getServer(getServer) {
      this.servers = JSON.parse(JSON.stringify(getServer));
    },
    server(server) {
      if (server) {
        let domain = server.server_name;
        if (domain.indexOf("https://") > -1)
          domain = domain.replace("https://", "");

        if (domain.indexOf("http://") > -1) {
          domain = domain.replace("http://", "");
        }
        this.subdomain = "." + domain;
      } else {
        this.subdomainValue = null;
      }
    },
    subdomainValue(subdomainValue) {
      this.subdomainInputValid = null;
      this.subdomainUnavailableReason = null;

      if (
        (subdomainValue && subdomainValue.includes(".")) ||
        (subdomainValue && subdomainValue.includes("_"))
      ) {
        // CL-18307: see passport_account/CreateCompany.vue -- an uncheckable
        // value must also abandon the probe already running for the previous
        // one, or its verdict lands beside the invalid value.
        this.cancelEntitySubdomainCheck();
        return false;
      }
      const completeSubdomain = `${subdomainValue}${this.subdomain}`
        .replace(/\s/g, "")
        .toLowerCase();
      const validSubdomain = isValidDomain(completeSubdomain, {
        subdomain: true,
        wildcard: false,
      });
      if (subdomainValue && validSubdomain && this.$v.subdomainValue.required && this.$v.subdomainValue.minLength && this.$v.subdomainValue.maxLength
          && this.$v.subdomainValue.validCharacters && !this.$v.subdomainValue.containsSpace
          && !this.$v.subdomainValue.containsHyphenAtStart && !this.$v.subdomainValue.containsDoubleHyphen) {
        this.form.entity.subdomain = completeSubdomain;
        this.subdomainInputTyping = true;
        this.checkEntitySubdomain({
          subdomain: completeSubdomain,
          server: this.server,
        })
          .then((outcome) => {
            // CL-18307: a superseded probe leaves the flag at null (unknown)
            // rather than reporting `false`, which used to flash "taken" at a
            // value the user had already typed past.
            if (!outcome || outcome.superseded) {
              return;
            }
            const body = (outcome.result && outcome.result.data) || {};
            this.subdomainInputValid = !!body.data;
            // CL-18308: falls back to 'taken' so this screen is correct
            // against a backend that does not send `reason` yet (vp5-back
            // #3296); 'reserved' lights up once that lands.
            this.subdomainUnavailableReason = unavailableReasonFrom(body);
          })
          .catch(() => {
            this.subdomainInputValid = null;
            this.subdomainUnavailableReason = null;
          });
      } else {
        this.cancelEntitySubdomainCheck();
      }
    },
    getIsValidSubdomain(value) {
      if (this.checkSubdomain && value) {
        this.openKioskInfo = true;
      } else this.openKioskInfo = false;
    },
    checkSubdomain(value) {
      if (this.getIsValidSubdomain && value) {
        this.openKioskInfo = true;
      } else this.openKioskInfo = false;
    },
    phone_input_company(phone_input) {
      this.form.entity.company_phone = `${this.companyCountryCode}${phone_input}`;
    },
    "form.user.email"(email) {
      this.inputTyping = false;
      setTimeout(() => {
        if (email && email.length > 0) {
          this.inputTyping = true;
          this.checkDuplicated({
            field: "email",
            value: email,
            });
        }
      }, 500);
    },
  },
  computed: {
    ...mapState("authfack", ["status"]),
    ...mapGetters("authfack", [
      "loadingStatus",
      "getEmailDuplicatedCheck",
      "getPhoneDuplicatedCheck",
      "getLoadingSubmitStatus",
    ]),
    ...mapGetters("entity", ["getIsValidSubdomain"]),
    ...mapGetters("server", ["getServer"]),
    notification() {
      return this.$store ? this.$store.state.notification : null;
    },
    checkSubdomain() {
      if (
        (this.subdomainValue && this.subdomainValue.includes(".")) ||
        (this.subdomainValue && this.subdomainValue.includes("_"))
      )
        return false;
      const completeSubdomain = `${this.subdomainValue}${this.subdomain}`
        .replace(/\s/g, "")
        .toLowerCase();
      const validSubdomain = isValidDomain(completeSubdomain, {
        subdomain: true,
        wildcard: false,
      });
      return validSubdomain ? true : false;
    },
    /**
     * CL-18308: does the typed value satisfy every FORMAT rule?
     *
     * Availability is deliberately not part of this -- see
     * `subdomainIssueMessage`. The two were ANDed into one condition whose
     * only failure message was "Invalid subdomain format.", so a well-formed
     * but already-taken subdomain was reported as malformed.
     */
    subdomainFormatValid() {
      const v = this.$v.subdomainValue;

      return !!(
        v.required &&
        v.minLength &&
        v.maxLength &&
        v.validCharacters &&
        !v.containsSpace &&
        !v.containsHyphenAtStart &&
        !v.containsDoubleHyphen
      );
    },
    /**
     * CL-18308: the one problem to show the user, or null when there is none.
     * Three distinct causes, three distinct messages; reserved is its own
     * answer rather than being reported as taken or as malformed.
     */
    subdomainIssueMessage() {
      if (!this.server || !this.subdomainValue || !this.subdomainInputTyping) {
        return null;
      }

      // Delegated so the two Add Company screens cannot drift apart on
      // wording or on precedence -- the duplication is what let this bug
      // exist on one screen while modal/CreateCompanyModal.vue still had the
      // distinction. See src/utils/subdomainFeedback.js.
      return subdomainIssue({
        formatValid: this.subdomainFormatValid,
        available: this.subdomainInputValid,
        reason: this.subdomainUnavailableReason,
      });
    },
    disableSubmitBtn() {
      const { user, entity } = this.form;
      return !(
        user.first_name &&
        user.last_name &&
        ((this.phone_input && !this.$v.phone_input.error && this.getPhoneDuplicatedCheck && this.getPhoneDuplicatedCheck.available) 
        || (user.email && !this.$v.form.user.email.error && this.$v.form.user.email.email && this.getEmailDuplicatedCheck && this.getEmailDuplicatedCheck.available)) &&
        entity.name &&
        entity.subdomain && this.terms_and_conditions
        && this.$v.subdomainValue.required && this.$v.subdomainValue.minLength && this.$v.subdomainValue.maxLength
          && this.$v.subdomainValue.validCharacters && !this.$v.subdomainValue.containsSpace
          && !this.$v.subdomainValue.containsHyphenAtStart && !this.$v.subdomainValue.containsDoubleHyphen
          && this.subdomainInputValid == true
      );
    },
    disableNextBtn() {
      const { user } = this.form;
      return !(
        user.first_name &&
        user.last_name &&
        ((this.phone_input && !this.$v.phone_input.error && this.getPhoneDuplicatedCheck && this.getPhoneDuplicatedCheck.available) 
        || (user.email && !this.$v.form.user.email.error && this.$v.form.user.email.email  && this.getEmailDuplicatedCheck && this.getEmailDuplicatedCheck.available)));
    },
  },
  methods: {
    ...mapActions("authfack", [
      "resetLoading",
      "saveKioskPackage",
      "saveUserPassword",
      "checkDuplicated",
      "registeruser"
    ]),
    ...mapActions("entity", [
      "checkDomain",
      "checkEntitySubdomain",
      "cancelEntitySubdomainCheck",
    ]),
    ...mapActions("server", ["getServers"]),
    validateState(name) {
      const { $dirty, $error } = this.$v.form.entity[name];
      return $dirty ? !$error : null;
    },
    addLamasatechSerial(selectedOption) {
      switch (selectedOption.name) {
        case "LD-AITemp":
          return (this.form.kiosk.lamasatech_serial = "7091135919162XXXXX");
        case "LT-Zentron8":
          return (this.form.kiosk.lamasatech_serial = "7091136435555XXXXX");
        case "Zentron 15":
          return (this.form.kiosk.lamasatech_serial = "5060861160027XXXXX");
        default:
          "";
      }
    },
    tryToRegisterIn() {
      this.submitted = true;
      // stop here if form is invalid
      this.$v.$touch();
      if (!this.$v.$invalid) {
        return;
      } else {
        this.isRegisterError = false;
        this.regError = null;
        // this.form.lamasatech_serial.id = this.form.kiosk.lamasatech_serial;
        this.registeruser({ form: this.form, server: this.server });
        if (localStorage.getItem("errors")) {
          this.regError = JSON.parse(localStorage.getItem("errors")).errors;
          this.isRegisterError = true;
        }
      }
    },
    beforeTabOneSwitch() {
      if (!this.$v.form.user.$invalid) {
        this.$v.$reset();
        this.submitted = false;
      } else {
        this.$v.$touch();
        this.submitted = true;
      }
      if (( this.getEmailDuplicatedCheck && this.getEmailDuplicatedCheck.available ) || ( this.getPhoneDuplicatedCheck && this.getPhoneDuplicatedCheck.available )) {
        return new Promise((resolve) => {
          resolve(!this.$v.form.user.$invalid);
        });
      }
    },
    beforeTabTwoSwitch() {
      this.subdomainInputTyping = true;
      if (!this.$v.form.entity.$invalid && !this.$v.subdomainValue.$invalid && !this.$v.server.$invalid) {
        this.$v.$reset();
        this.submitted = false;
      } else {
        this.$v.$touch();
        this.submitted = true;
        return;
      }
      if (this.checkSubdomain && this.getIsValidSubdomain) {
        return new Promise((resolve) => {
          this.openKioskInfo = true;
          resolve(!this.$v.form.entity.$invalid);
        });
      }
    },
    beforeTabThreeSwitch() {
      if (!this.$v.form.kiosk.$invalid) {
        this.$v.$reset();
        this.submitted = false;
      } else {
        this.$v.$touch();
        this.submitted = true;
      }
      return new Promise((resolve) => {
        resolve(!this.$v.form.kiosk.$invalid);
      });
    },
    oninputTyping(e) {
      console.log('value', e.srcElement.value);
      this.inputTyping = false;
      if(e.srcElement.value)
      return (this.inputTyping = true);
    },
    onPhoneInputTyping() {
      this.phoneInputTyping = false;
            if (this.phone_input && this.phone_input.length > 0) {
        this.form.user.phone = `${this.userCountryCode}${this.phone_input}`;
        this.phoneInputTyping = true;
        setTimeout(() => {
            this.checkDuplicated({
              field: "phone",
              value: `${this.userCountryCode}${this.phone_input}`,
            });
        }, 500);
      } else {
        this.form.user.phone = null;
      }
    },
    oninputPaste() {
      return (this.inputTyping = true);
    },
    onSubdomainInputTyping() {
      return (this.subdomainInputTyping = true);
    },
    onSubdomainInputPaste() {
      return (this.subdomainInputTyping = true);
    },
    onSelectUserCountryCode({ dialCode }) {
      this.userCountryCode = dialCode;
      this.onPhoneInputTyping();
    },
    onSelectCompanyCountryCode({ dialCode }) {
      this.companyCountryCode = dialCode;
    },
    onTypeChange(value) {
      this.form.typeValue = value
      console.log(this.form.typeValue);
    },
    changeServerVal() {
      this.subdomainValue = null;
    }
  },
  mounted() {
    this.$store.commit("authfack/setLoadingSubmitStatus", false);
    this.resetLoading();
    this.getServers()
      .then((data) => {
        this.servers = data.data.data;
        this.servers.map(async (server) => {
          let name = server.server_name;
          if (localStorage.getItem(name) !== "undefined") {
            if (name.indexOf("https://") > -1)
              name = name.replace("https://", "");
            if (name.indexOf("http://") > -1)
              name = name.replace("http://", "");
            if (!JSON.parse(localStorage.getItem(name))) {
              let secretkey = await UserService.getSecretKey(server.server_api)
                .then((data) => {
                  if (data.data.data) return data.data.data;
                })
                .catch((err) => {
                  console.log(err);
                });
              let cloudAccesstoken = await UserService.getCloudToken(
                server.server_api,
                secretkey
              )
                .then((data2) => {
                  if (data2.data.data) return data2.data.data.access_token;
                })
                .catch((err) => {
                  console.log(err);
                });
              localStorage.setItem(name, JSON.stringify(cloudAccesstoken));
            }
          }
        });
      })
      .catch((error) => {
        console.log(error);
      });
  },
};
</script>

<template>
  <div class="signup-wrapper">
    <div class="signup">
      <div class="header">
        <div class="logo">
          <img
            src="@/assets/images/logo-lg.svg"
            class="unselectable mx-auto"
            alt
            width="88px"
            height="16px"
          />
        </div>
        <p class="header-title"> Company Dashboard </p>
        <p class="subtitle">Register</p>
      </div>
      <form-wizard
        class="custom-form-wizard form"
        
        color="#103F5E"
        step-size="sm"
      >
        <!-- Step 1  -->
        <tab-content :before-change="beforeTabOneSwitch">
          <p class="tab-title">Personal details</p>
          <div class="">
            <!-- first name  -->
            <div class="first-name-wrapper mb-16">
              <div
                class="form-group m-d-gruop"
                :class="$v.form.user.first_name.$error && 'invalid-value mb-8'"
              >
                <b-form-input
                  id="first_name"
                  v-model.trim="form.user.first_name"
                  type="text"
                  required
                  class="form-control"
                  maxLength="20"
                  :class="{'cursor-not-allowed':form.user.first_name && form.user.first_name.length >= 20}"
                />
                <label for="first_name">{{ $t('common.firstName')}}</label>
              </div>
              <div class="err-wrapper">
                <div v-if="submitted && !$v.form.user.first_name.required">
                  <span class="error-value">{{ $t('validation.requiredValidation',{value: $t('common.firstName')})}}</span>
                </div>
                <div
                  v-else-if="submitted && !$v.form.user.first_name.maxLength"
                >
                  <span class="error-value"
                    >{{$t('validation.maxLen',{value: 20})}}</span
                  >
                </div>
              </div>
            </div>
            <!-- last name  -->
            <div class="last-name-wrapper mb-0">
              <div
                class="form-group m-d-gruop mb-0"
                :class="$v.form.user.last_name.$error && 'invalid-value mb-8'"
              >
                <b-form-input
                  id="form.last_name"
                  v-model.trim="form.user.last_name"
                  type="text"
                  required
                  class="form-control"
                  maxLength="20"
                  :class="{'cursor-not-allowed':form.user.last_name && form.user.last_name.length >= 20}"
                />
                <label for="form.last_name">{{ $t('common.lastName')}}</label>
              </div>
              <div class="err-wrapper">
                <div v-if="submitted && !$v.form.user.last_name.required">
                  <span class="error-value">{{ $t('validation.requiredValidation',{value: $t('common.lastName')})}}</span>
                </div>
                <div v-else-if="submitted && !$v.form.user.last_name.maxLength">
                  <span class="error-value"
                    >{{$t('validation.maxLen',{value: 20})}}</span
                  >
                </div>
              </div>
            </div>
          </div>
          <hr class="h-line"/>
          <p class="hint-label">Add Email Address or Phone Number</p>
          <!-- email   -->
          <div class="email-wrapper mb-16">
            <div
              class="form-group m-d-gruop"
              :class="
                ($v.form.user.email.$error ||
                (inputTyping && getEmailDuplicatedCheck && !getEmailDuplicatedCheck.available && getEmailDuplicatedCheck.msg.length==0)) &&
                  'invalid-value mb-8'
              "
            >
              <b-form-input
                id="email"
                v-model.trim="form.user.email"
                type="text"
                required
                class="form-control"
                @input="$v.form.user.email.$touch()"
                @keypress="oninputTyping($event)"
                @paste="oninputPaste"
              />
              <label for="email">{{ $t('common.email') }}</label>
            </div>
            <div class="err-wrapper" 
            >
              <div v-if="inputTyping && !$v.form.user.email.required">
                <span class="error-value">{{ $t('validation.requiredValidation',{value: $t('common.Email')})}}</span>
              </div>
              <div v-else-if="inputTyping && !$v.form.user.email.email">
                <span class="error-value">Invalid email format.</span>
              </div>
              <div v-else-if="inputTyping && form.user.email && (getEmailDuplicatedCheck&&!getEmailDuplicatedCheck.available&&getEmailDuplicatedCheck.msg.length==0)">
                <span class="error-value"
                  >Email address already exists, register with a different one or 
                  <router-link tag="a" to="/login" class="redirect-login">Login</router-link
                  ></span>
              </div>
              <div v-else-if="inputTyping && form.user.email && (getEmailDuplicatedCheck&&getEmailDuplicatedCheck.msg.length>0)">
                <span class="error-value"
                  >A deactivated account with this email address already exists,
                  login to reactivate your account.
                  <router-link tag="a" to="/login" class="redirect-login">Login</router-link
                  ></span>
              </div>
            </div>
          </div>
          <!-- phone  -->
          <div class="input-wrapper current-phone"
          :class="
                  ($v.phone_input.$error || !$v.phone_input.numeric ||
                    (phoneInputTyping && getPhoneDuplicatedCheck && !getPhoneDuplicatedCheck.available)) &&
                    'mb-111'
                ">
            <div class="row-group phone-group justify-content-start w-100 mb-0">
              <VueCountryCode
                class="country-code-input"
                :disabledFetchingCountry="false"
                @onSelect="onSelectUserCountryCode"
                :enabledCountryCode="true"
                :preferredCountries="['GB', 'US', 'CN']"
              />
              <div
                class="form-group m-d-gruop w-100 mb-0"
                :class="
                  ($v.phone_input.$error || !$v.phone_input.numeric ||
                    (phoneInputTyping && getPhoneDuplicatedCheck && !getPhoneDuplicatedCheck.available)) &&
                    'invalid-value mb-8 '
                "
              >
                <b-form-input
                  id="phone"
                  v-model.trim="phone_input"
                  type="text"
                  required
                  
                  @input="onPhoneInputTyping"
                />
                <label for="phone">Phone Number</label>
                <div class="err-wrapper mt-6"
                  >
                  <div
                    v-if="
                      ($v.phone_input.$error || phoneInputTyping) &&
                        !$v.phone_input.required
                    "
                  >
                    <span class="error-value"
                      >Phone number or email is required</span
                    >
                  </div>
                  <div class="w-245" v-else-if="phoneInputTyping && phone_input && (getPhoneDuplicatedCheck&&!getPhoneDuplicatedCheck.available&&getPhoneDuplicatedCheck.msg.length==0)">
                    <span class="error-value"
                      >Phone number already exists, register with a different one or
                      <router-link tag="a" to="/login" class="redirect-login">Login</router-link
                      ></span>
                  </div>
                  <div class="w-245" v-else-if="phoneInputTyping && phone_input && (getPhoneDuplicatedCheck&&getPhoneDuplicatedCheck.msg.length>0)">
                    <span class="error-value"
                      >A deactivated account with this phone number already exists,
                      login to reactivate your account.
                      <router-link tag="a" to="/login" class="redirect-login">Login</router-link
                      ></span>
                  </div>
                  <div class="w-245" v-else-if="!$v.phone_input.numeric">
                    <span class="error-value">
                      Phone number should contain only digits.
                    </span>
                  </div>
                </div>
              </div>
            </div>

          </div>
        </tab-content>
        <!-- Step 2  -->
        <tab-content :before-change="beforeTabTwoSwitch">
          <p class="tab-title">{{ $t('common.companyDetails') }}</p>
          <!-- name   -->
          <div class="user-name company-input">
            <div class="input-wrapper">
              <div
                class="form-group m-d-gruop"
                :class="$v.form.entity.name.$error && 'invalid-value mb-8'"
              >
                <b-form-input v-model="form.entity.name" type="text" required maxlength="50" />
                <label for="phone">Company Name</label>
              </div>
            </div>
            <div class="err-wrapper">
              <div v-if="submitted && !$v.form.entity.name.required">
                <span class="error-value">{{ $t('validation.requiredValidation',{value: $t('common.companyName')})}}</span>
              </div>
              <div v-else-if="submitted && !$v.form.entity.name.maxLength">
                <span class="error-value"
                  >The maximum length is 50 characters.</span
                >
              </div>
            </div>
          </div>
          <!-- Server -->
          <div class="company-input select-server">
            <div
              class="input-wrapper multiselect-container"
              :class="$v.server.$error && 'invalid-value mb-8'"
            >
            <svg
              width="15"
              height="15"
              viewBox="0 0 15 15"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              class="multi-select-placeholder-icon"
            >
              <path
                d="M13.7467 15C14.0674 15 14.3884 14.8777 14.6329 14.6328C15.1224 14.1435 15.1224 13.3498 14.6329 12.8603L11.586 9.8135C13.2154 7.3595 12.9497 4.00983 10.7883 1.8485C8.32362 -0.616167 4.31323 -0.616167 1.84853 1.8485C-0.616175 4.31317 -0.616175 8.3235 1.84853 10.7883C3.08088 12.0207 4.69973 12.6368 6.31842 12.6368C7.53877 12.6368 8.75879 12.2863 9.81364 11.586L12.8605 14.6328C13.105 14.8777 13.4259 15 13.7467 15ZM3.40305 9.23367C1.79569 7.62633 1.79569 5.01083 3.40305 3.4035C4.20673 2.59983 5.26257 2.19783 6.31825 2.19783C7.37394 2.19783 8.42978 2.59967 9.23363 3.4035C10.841 5.01083 10.841 7.62633 9.23363 9.23367C7.62611 10.841 5.01057 10.841 3.40305 9.23367Z"
                fill="#787878"
              />
            </svg>
            <!-- <span
              v-if="server"
              :class="{'ml-51 mt-12': server }"
              class="multiselect-title custom-multiselect-title"
              >Server
            </span> -->
              <multiselect
                id="server"
                class="custom-multi-select"
                :class="{'hide-placeholder': !server }"
                v-model="server"
                :options="servers"
                placeholder="Server"
                :show-labels="false"
                :allowEmpty="true"
                @select="changeServerVal"
                @deselect="changeServerVal"
              >
                <template slot="option" slot-scope="props">
                  <div class="option__desc">
                    <span
                      class="option__name font-weight-bold"
                      >{{ props.option.server_name }} ({{ props.option.notes }})</span
                    >
                  </div>
                </template>

                <template slot="singleLabel" slot-scope="{ option }">
                  <div class="option__desc">
                    <span
                      class="option__name font-weight-bold"
                      >{{ option.server_name }} ({{ option.notes }})</span
                    >
                  </div>
                </template>
              </multiselect>
            </div>
            <div class="err-wrapper" v-if="submitted && !$v.server.required">
              <span class="error-value"> {{ $t('validation.requiredValidation',{value: $t('common.server')})}}</span>
            </div>
          </div>
          <!-- subdomain  -->
          <div class="user-subdomain company-input">
            <div class="input-wrapper">
              <div class="row-group phone-group justify-content-start h-55">
                <div
                  class="form-group m-d-gruop w-100 mb-0 subdomain-input"
                  :class=" server && subdomainValue ? $v.subdomainValue.required && $v.subdomainValue.minLength && $v.subdomainValue.maxLength
                  && $v.subdomainValue.validCharacters && !$v.subdomainValue.containsSpace
                  && !$v.subdomainValue.containsHyphenAtStart && !$v.subdomainValue.containsDoubleHyphen && ( subdomainInputValid === true || subdomainInputValid === null)?
                    '' :'invalid-value-subdomain mb-8' : ''"
                >
                  <b-form-input
                    id="subdomain"
                    autocomplete="off"
                    :disabled="!server"
                    v-model="subdomainValue"
                    type="text"
                    required
                    autofocus
                    name="subdomain"
                    @keypress="onSubdomainInputTyping"
                    @paste="onSubdomainInputPaste"
                    @input="$v.subdomainValue.$touch()"
                    maxlength="63"
                    minlength="3"
        
                  />
                  <label for="subdomain" :class="server ? 'subdomain-label' : 'disabled-subdomain-input'">Subdomain</label>
                </div>
                <div class="subdomain-server m-d-gruop" v-if="server" :class=" server && subdomainValue ? $v.subdomainValue.required && $v.subdomainValue.minLength && $v.subdomainValue.maxLength
                  && $v.subdomainValue.validCharacters && !$v.subdomainValue.containsSpace
                  && !$v.subdomainValue.containsHyphenAtStart && !$v.subdomainValue.containsDoubleHyphen && ( subdomainInputValid === true || subdomainInputValid === null)?
                    '' :'invalid-value-subdomain-name mb-8' : ''">
                  <p class="mb-0">{{ subdomain }}</p>
                </div>
              </div>
              <div class="d-flex" :class="server ? 'subdomain-requirements-container' : 'disabled-subdomain-requirements-container'" >
                <svg
                  id="subdomainContainer"
                  width="16"
                  height="16"
                  viewBox="0 0 17 17"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                  class="subdomain-requirements-icon"
                  v-b-tooltip
                >
                  <path
                    d="M8.49904 5.41102e-08C6.81794 0.00018973 5.17466 0.498867 3.77697 1.43297C2.37928 2.36708 1.28996 3.69466 0.646766 5.24785C0.00356934 6.80103 -0.164642 8.51007 0.163441 10.1588C0.491523 11.8076 1.30114 13.3221 2.48992 14.5107C3.67871 15.6994 5.19327 16.5088 6.84208 16.8367C8.49089 17.1646 10.1999 16.9962 11.753 16.3529C13.3061 15.7095 14.6336 14.62 15.5676 13.2222C16.5015 11.8244 17 10.1811 17 8.49998C17 7.38367 16.7801 6.27828 16.3529 5.24696C15.9256 4.21563 15.2995 3.27855 14.5101 2.48924C13.7207 1.69993 12.7835 1.07385 11.7521 0.646742C10.7208 0.219634 9.61535 -0.000125898 8.49904 5.41102e-08ZM9.61057 12.9471C9.61057 13.2419 9.49347 13.5246 9.28502 13.733C9.07657 13.9415 8.79383 14.0586 8.49904 14.0586C8.20424 14.0586 7.92151 13.9415 7.71306 13.733C7.5046 13.5246 7.38751 13.2419 7.38751 12.9471V7.67234C7.38751 7.37754 7.5046 7.09481 7.71306 6.88635C7.92151 6.6779 8.20424 6.56079 8.49904 6.56079C8.79383 6.56079 9.07657 6.6779 9.28502 6.88635C9.49347 7.09481 9.61057 7.37754 9.61057 7.67234V12.9471ZM8.49904 5.16732C8.27976 5.16675 8.06555 5.10127 7.88342 4.97914C7.7013 4.85702 7.55939 4.68371 7.47561 4.48107C7.39183 4.27842 7.36991 4.05551 7.41262 3.84043C7.45534 3.62534 7.56078 3.42773 7.71563 3.27247C7.87049 3.11722 8.06784 3.01127 8.28281 2.968C8.49778 2.92474 8.72073 2.94609 8.9236 3.02935C9.12646 3.11261 9.30013 3.25405 9.42273 3.43586C9.54532 3.61767 9.61136 3.83171 9.6125 4.05099C9.61313 4.19748 9.58476 4.34264 9.52905 4.47812C9.47335 4.61361 9.39138 4.73673 9.28789 4.8404C9.18439 4.94407 9.06141 5.02625 8.92602 5.08219C8.79064 5.13813 8.64553 5.16673 8.49904 5.16635V5.16732Z"
                    :fill=" server && subdomainValue ? $v.subdomainValue.required && $v.subdomainValue.minLength && $v.subdomainValue.maxLength
                    && $v.subdomainValue.validCharacters && !$v.subdomainValue.containsSpace
                    && !$v.subdomainValue.containsHyphenAtStart && !$v.subdomainValue.containsDoubleHyphen && this.subdomainInputValid !== false ?   '#787878' :  '#F46A6A' : server ? '#787878' : '#C1C1C1'"
                  />
                </svg>

                <span :class="server ? 'subdomain-requirements-label' : 'disabled-subdomain-label'"

                v-if="!server || !subdomainValue"
                >Subdomain requirements</span>

                <!-- CL-18308: one message per cause. Previously every
                     format rule was ANDed with `subdomainInputValid !== false`
                     and fell through to a single v-else reading "Invalid
                     subdomain format." for all of them, so a taken subdomain
                     was reported as a malformed one. -->
                <span :class="server ? 'subdomain-requirements-label' : 'disabled-subdomain-label'"
                v-else-if="!subdomainIssueMessage"
                >Subdomain requirements</span>

                <span v-else class="subdomain-requirements-label-error" >{{ subdomainIssueMessage }}</span>


              </div>
              <b-tooltip
                  placement="bottomleft"
                  target="subdomainContainer"
                  triggers="click blur"
                >
                  <div class="subdomain-requirements">
                      <span class="subdomain-requirements-title">Subdomain requirements:</span>  
                      <div class="subdomain-requirements-items-container">
                        <span class="subdomain-requirements-item"
                        :class="{'failure-requirements-item': subdomainInputTyping && subdomainValue &&  subdomainValue.length > 0 && (!$v.subdomainValue.required || !$v.subdomainValue.minLength || !$v.subdomainValue.maxLength) , 
                        'success-requirements-item': subdomainInputTyping &&  subdomainValue && subdomainValue.length > 0 && $v.subdomainValue.required && $v.subdomainValue.minLength && $v.subdomainValue.maxLength}">
                        - Must be 3 to 63 characters long. 
                      </span>
                      <hr class="subdomain-requirements-hr" />

                        <span class="subdomain-requirements-item"
                          :class="{'failure-requirements-item': subdomainInputTyping && subdomainValue && subdomainValue.length > 0 && !$v.subdomainValue.validCharacters , 
                          'success-requirements-item': subdomainInputTyping && subdomainValue && subdomainValue.length > 0 && $v.subdomainValue.validCharacters}">
                          - Use only lowercase letters (a-z), numbers (0-9), and hyphens (-). 
                        </span>
                        <hr class="subdomain-requirements-hr" />

                        <span class="subdomain-requirements-item" 
                          :class="{'failure-requirements-item': subdomainInputTyping && subdomainValue && subdomainValue.length > 0 && $v.subdomainValue.containsSpace , 
                          'success-requirements-item': subdomainInputTyping && subdomainValue && subdomainValue.length > 0 && !$v.subdomainValue.containsSpace}">
                          - Cannot contain spaces or special characters (@, #, $, %, etc.).
                        </span>

                        <hr class="subdomain-requirements-hr" />
                        <span class="subdomain-requirements-item"
                        :class="{'failure-requirements-item': subdomainInputTyping && subdomainValue && subdomainValue.length > 0 && $v.subdomainValue.containsHyphenAtStart , 
                          'success-requirements-item': subdomainInputTyping && subdomainValue && subdomainValue.length > 0 && !$v.subdomainValue.containsHyphenAtStart}">
                          - Cannot start or end with a hyphen. 
                        </span>
                        <hr class="subdomain-requirements-hr" />
                        <span class="subdomain-requirements-item" :class="{'failure-requirements-item': subdomainInputTyping && subdomainValue && subdomainValue.length > 0 && $v.subdomainValue.containsDoubleHyphen , 
                          'success-requirements-item': subdomainInputTyping && subdomainValue && subdomainValue.length > 0 && !$v.subdomainValue.containsDoubleHyphen}">
                          - No consecutive hyphens (e.g., my--subdomain is not allowed).
                        </span>
                        <hr class="subdomain-requirements-hr" />

                        <span class="subdomain-requirements-item"
                        :class="{'failure-requirements-item': subdomainInputTyping && subdomainValue && subdomainValue.length > 0 && subdomainInputValid === false && subdomainUnavailableReason !== 'reserved' ,
                         'success-requirements-item': subdomainInputTyping && subdomainValue && subdomainValue.length > 0 && subdomainInputValid === true }">
                         - Must be unique (already taken subdomains are not allowed).
                        </span>

                      </div>
                  </div>
                </b-tooltip>
            </div>
            <!-- <div class="err-wrapper"> -->
              <!-- <div v-if="subdomainInputTyping && !$v.subdomainValue.required">
                <span class="error-value">Subdomain is required.</span>
              </div> -->
              <!-- <div v-else-if="subdomainInputTyping && !$v.subdomainValue.containsSpace">
                  <span class="error-value">Subdomain should not includes any spaces.</span>
              </div> -->
              <!-- <div v-else-if="subdomainInputTyping && !checkSubdomain">
                <span class="error-value">
                  {{ $t('validation.subdomainInvalid')}}
                </span>
              </div> -->
              <!-- <div v-else-if="subdomainInputTyping && !getIsValidSubdomain">
                <span class="error-value">
                  {{ $t('validation.subdomainUsed')}}
                </span>
              </div> -->
            <!-- </div> -->
          </div>
          <!-- phone  -->
          <div class="company-input">
            <div class="input-wrapper">
              <div class="row-group phone-group justify-content-start">
                <VueCountryCode
                  class="country-code-input"
                  :disabledFetchingCountry="false"
                  @onSelect="onSelectCompanyCountryCode"
                  :enabledCountryCode="true"
                  :preferredCountries="['GB', 'US', 'CN']"
                />
                <div class="form-group m-d-gruop w-100">
                  <b-form-input
                    id="phone"
                    v-model.trim="phone_input_company"
                    type="number"
                    required
                  />
                  <label for="phone">{{ $t('common.companyPhone')}} ({{ $t('common.optional') }})</label>
                </div>
              </div>
            </div>
            <div class="err-wrapper">
              <div v-if="submitted && !$v.phone_input_company.numeric">
                <span class="error-value">
                  {{ $t('validation.phoneDigitsOnly') }}
                </span>
              </div>
            </div>
          </div>
          <!-- entity checkbox  -->
          <div class="custom-entity-checkbox" >
            <b-form-checkbox  id="UpdateCloudFromEmailCheckbox" v-model="terms_and_conditions" >
              <span class="ml-9" :class="{'custom-entity-checkbox-checked':terms_and_conditions}">I agree to</span> <a target="_blank" href="https://www.visipoint.net/privacy-policy" class="redirect-login">Privacy Policy</a> 
              
              <span :class="{'custom-entity-checkbox-checked':terms_and_conditions}"> and </span> <a target="_blank" href="https://www.visipoint.net/terms-and-conditions-of-sale" class="redirect-login">Terms and Conditions</a
                  >
            </b-form-checkbox>
          </div>
        </tab-content>

        <!-- actions  -->
        <!-- <p class="slot-prev mb-0" slot="prev">{{ $t('common.back')}}</p> -->
        <!-- <p :disabled="disableNextBtn && loadingStatus" class="slot-next mb-0" :class="{'disabled-next': disableNextBtn}" slot="next">
          {{ $t('common.next')}}</p> -->

        <!-- Wizard footer -->
        <div slot="footer" slot-scope="props">
          
          <div class="right-left-position" >

            <div class="wizard-footer-left">
              <wizard-button :disabled="props.activeTabIndex == 0" 
              @click.native="props.prevTab()" class="slot-prev mb-0" :class="{'disabled-next': props.activeTabIndex == 0}">
                {{ $t("common.back") }}
              </wizard-button>
            </div>

            <div class="wizard-footer-right" style="display: block;">
              <wizard-button :disabled="disableNextBtn || props.activeTabIndex > 0" 
              @click.native="props.nextTab()" :class="{'disabled-next': disableNextBtn || props.activeTabIndex > 0}" class="slot-next mb-0" style="display: block;">
                {{ $t("common.next") }}
              </wizard-button>
            </div>
            
          </div>
          
        </div>
      </form-wizard>
      <button
        class="btn btn-primary btn-block submit-btn"
        type="button"
        @click="tryToRegisterIn"
        :disabled="getLoadingSubmitStatus || disableSubmitBtn"
      >
        <i
          v-if="getLoadingSubmitStatus"
          class="bx bx-loader bx-spin font-size-16 align-middle mr-2 rounded"
        ></i>
        <span v-else>{{ $t('common.register').toUpperCase()}}</span>
      </button>
      <!-- login if has acc  -->
      <div class="login-wrapper">
        <p class="login-title">{{  $t('form.alreadyHaveAccount') }}</p>
        <router-link tag="a" to="/login" class="login-redirect"
          >{{ $t('common.login').toUpperCase()}}</router-link
        >
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.cursor-not-allowed{
  cursor: not-allowed;
}
.show-icon {
  display: block !important;
}
.hide-icon {
  display: none !important;
}
.input-icons i {
  position: relative;
  float: right;
  padding-right: 15px;
}
  
.input-icons {
  width: 100%;
  margin-bottom: 10px;
}
  
.icon {
  // padding: 10px;
  margin: -16px;
  color: #34c38f;
  min-width: 50px;
  text-align: center;
}
  
.input-field {
  width: 100%;
  padding: 10px;
  // text-align: center;
}



.signup-wrapper {
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 30px 0;
  .signup {
    width: 500px;
    border-radius: 26px;
    background-color: #FFFFFF;
    padding: 40px 42px;
    padding-bottom: 38px;

    .header {
      text-align: center;
      margin-bottom: 40px;
      .logo {
        margin-bottom: 24px;
        margin-right: auto;
        margin-left: auto;
      }
      .header-title {
        margin-bottom: 24px;
        font-weight: 700;
        font-size: 17px;
        line-height: 100%;
        letter-spacing: 0%;
        text-align: center;
      }
      .subtitle {
        font-weight: 700;
        font-size: 16px;
        color: #787878;
      }
    }
    .login-redirect {
      font-weight: bold;
      font-size: 14px;
      letter-spacing: 0.25px;

      color: #103f5e;
      text-decoration: underline;
    }
    .form-group {
      margin-bottom: 16px;
    }
    .h-line {
      margin-top: 24px !important;
      margin-bottom: 24px !important;
      color: red;
    }
    .tab-title {
      margin-bottom: 32px;
      font-family: Lato;
      font-weight: 700;
      font-size: 16px;      
      line-height: 100%;
      letter-spacing: 0.25px;
      vertical-align: middle;
      color: #787878;
    }
    .hint-label {
      margin-bottom: 24px;
      font-family: Lato;
      font-weight: 400;
      font-size: 14px;
      line-height: 20px;
      letter-spacing: 0.25px;
      vertical-align: middle;
      color: #787878;

    }
    .company-input {
      margin-bottom: 16px;
      width: 100%;
    }
    .select-server,
    .select-device {
      .err-wrapper {
        margin: 10px 0;
      }
      .input-wrapper {
        display: flex;
        align-items: center;
        background-color: #f3f3f3;
        height: 55px;
      }
    }
    .form-control:disabled,
    .form-control[readonly] {
      background-color: #f3f3f3 !important;
    }
    .phone-input-wrapper {
      display: grid;
      grid-template-columns: 20% 80%;
    }
    .kiosk-date-wrapper {
      height: 72px;
    }
    .kiosk-date-err {
      height: 96px;
      margin-bottom:25px;
    }
    .kiosk-name {
      margin-right: 16px;
    }
    .purchased-from-text {
      font-weight: bold;
      font-size: 16px;
      letter-spacing: 0.25px;
      color: #787878;
      margin-bottom: 29px;
    }
    .checks,
    .purchased-from-text {
      padding-left: 20px;
    }
    .checks{
      margin-bottom: 5px;
    }
    .submit-btn {
      width: 100%;
      height: 44px;
      font-size: 16px;
      border-radius: 22px;
      text-transform: uppercase;
      font-weight: bold;
      color: rgba(255, 255, 255, 0.87);
      margin-bottom: 0;
      margin-top: 27px;
    }
    .login-wrapper {
      text-align: center;
      margin-top: 20px;
      .login-title {
        color: rgba(0, 0, 0, 0.28);
        font-size: 16px;
        font-weight: 500;
        margin-bottom: 22px;
      }
      .login-redirect {
        font-weight: bold;
        font-size: 16px;
        text-align: center;
        text-transform: uppercase;
        color: #103f5e;
        margin-bottom: 0;
      }
    }  
  }

  .mb-0{
    margin-bottom: 0px !important;
  }

  .mb-8{
    margin-bottom: 8px !important;
  }
  .mb-16{
    margin-bottom: 16px !important;
  }

  .mb-40 {
    margin-bottom: 40px !important;
  }

  .form-group {
    margin-bottom: 0px !important;
  }

  .w-245{
    width: 245px !important;
  }

  .mb-111{
    margin-bottom: 111px !important;
  }

  .mt-6{
    margin-top: -6px !important;
  }

  .subdomain-input{
    border-start-end-radius: 0px !important;
    border-end-end-radius: 0px !important;
  }

  .custom-multiselect-title{
    font-family: Lato;
    font-weight: 700;
    font-size: 12px;
    color: #103F5E;
    z-index: 1 !important;
  }


  .pl-24 {
    padding-left: 24px !important;
  }
  
  .ml-51{
    margin-left: 51px !important;
  }

  .mt-12{
    margin-top: 12px !important;
  }

  .select-server{
    position: relative;
  }

 
  .subdomain-requirements-container{
    align-items: center;
    margin-top: 8px;
    margin-left: 20px;
  }

  .disabled-subdomain-requirements-container{
    align-items: center;
    margin-top: 8px;
    margin-left: 20px;
    cursor: not-allowed; //cursor-not-allowed
    pointer-events: none;
  }

  .subdomain-requirements-icon{
    margin-right: 8px;
    cursor: pointer;

    &:focus{
      outline: none;
    }
  }  

  .disabled-subdomain-label {
    color: #C1C1C1 !important;
    font-size: 14px !important;
    font-weight: 400 !important;
  }

  .disabled-subdomain-input {
    color: #C1C1C1 !important;
  }

  .invalid-value-subdomain {
    border: 1.5px solid #F46A6A !important;
    border-right: none !important;
  }
  .invalid-value-subdomain-name {
    border: 1.5px solid #F46A6A !important;
    border-left: none !important;
  }

  .subdomain-requirements-label{
    font-family: Lato !important;
    font-weight: 400 !important;
    font-size: 14px !important;
    line-height: 22px !important;
    color: #787878;
  }

  .subdomain-requirements-label-error{
    color: #F46A6A !important;
    font-family: Lato;
    font-weight: 600;
    font-size: 14px;
    line-height: 22px;
  }


  .disabled-next {
    color: #9FB2BF !important;
    pointer-events: none !important;
    cursor: not-allowed !important;
  }

  .vue-form-wizard .wizard-btn.disabled, .vue-form-wizard .wizard-btn[disabled], fieldset[disabled] {
    opacity: unset !important;
  }

  .vue-form-wizard .navbar .navbar-nav>li>a.wizard-btn, .vue-form-wizard .wizard-btn {
    padding: 0px !important;
  }

  .vue-form-wizard .navbar .navbar-nav>li>a.wizard-btn, .vue-form-wizard .wizard-btn {
    min-width: 35px !important;
  }
}

</style>


<style lang="scss">


  .tooltip {
    // top: -24px !important;
    // left: 122px !important;
    top: -18px !important;
    left: 311px !important;
    border-radius: 6px;
    box-shadow: 0px 1px 40px 3px rgba(0, 0, 0, 0.05) !important;
    background-color: #FFFFFF !important;
    padding: 0px !important;
    .tooltip-inner {
      width: 302px !important;
      max-width: 302px !important;
      color: #787878 !important;
      padding: 16px !important;  
      font-family: 'Lato';
      background-color: #FFFFFF !important;
    }
    .arrow{
      display: none !important;
    }

    .subdomain-requirements{
      text-align: left;
      
    }

    .subdomain-requirements-title{
      font-family: Lato;
      font-weight: 700;
      font-size: 12px;
      line-height: 17px;
      color: #787878;
      text-align: left;
    }

    .subdomain-requirements-items-container{
      margin-top: 13px;
    }

    .subdomain-requirements-item{
      font-family: Lato;
      font-weight: 400;
      font-size: 12px;
      line-height: 17px;
      color: #787878;      
    }

    .subdomain-requirements-hr{
      margin-top: 10px;
      margin-bottom: 10px;
      color: #EBEBEB;
      border: 1px solid #EBEBEB
    }


    .success-requirements-item{
      color: #34C38F !important;
      font-weight: 700 !important;
    }

    .failure-requirements-item{
      color : #F46A6A !important;
      font-weight: 700 !important;
    }
    
  }

</style>